import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login, getCurrentUser, clearUser } from '../features/auth/authSlice';
import { needsEmailVerification } from '../utils/authVerification';
import { Card, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import ParticlesAuth from './auth/ParticlesAuth';

function Login() {
  console.log('Login component rendering...');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, status } = useSelector((state) => state.auth);
  console.log('Login: Redux hooks initialized successfully');
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasLoggedIn, setHasLoggedIn] = useState(false);

  // Validate session with backend only on mount (not after login)
  useEffect(() => {
    // Skip validation if user just logged in or if we already have valid user data
    if (hasLoggedIn || (user && token && status === 'succeeded')) {
      if (user && token) {
        const from = location.state?.from?.pathname || '/dashboard';
        const target =
          user?.must_change_password ||
          localStorage.getItem('auth_must_change_password') === 'true'
            ? '/auth/complete-invited-password-setup'
            : needsEmailVerification(user) ||
              localStorage.getItem('auth_verification_required') === 'true'
              ? '/verify-email'
              : from;
        navigate(target, { replace: true });
      }
      setCheckingSession(false);
      return;
    }

    const validateSession = async () => {
      const storedToken = localStorage.getItem('auth_token');
      
      // If no token exists, user is not logged in
      if (!storedToken && !token) {
        setCheckingSession(false);
        return;
      }

      // If we have a token, validate it with the backend
      if (storedToken || token) {
        try {
          // Validate session by fetching current user from backend
          const result = await dispatch(getCurrentUser()).unwrap();
          if (result && (result.user?.id || result.id || result.user_id)) {
            const resolvedUser = result.user || result;
            const from = location.state?.from?.pathname || '/dashboard';
            const target =
              result.mustChangePassword ||
              resolvedUser?.must_change_password ||
              localStorage.getItem('auth_must_change_password') === 'true'
                ? '/auth/complete-invited-password-setup'
                : result.verificationRequired || needsEmailVerification(resolvedUser)
                  ? '/verify-email'
                  : from;
            navigate(target, { replace: true });
          } else {
            // Invalid session, clear data
            dispatch(clearUser());
            setCheckingSession(false);
          }
        } catch (error) {
          console.log('Session validation failed on login page:', error);
          if (error?.type === 'verification_required') {
            navigate('/verify-email', { replace: true });
          } else {
            dispatch(clearUser());
          }
          setCheckingSession(false);
        }
      } else {
        setCheckingSession(false);
      }
    };

    validateSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('Submitting login form with:', { email: formData.email, password: '***' });
      const result = await dispatch(login(formData)).unwrap();
      console.log('Login successful, result:', result);
      
      if (result?.token) {
        setHasLoggedIn(true);
        setCheckingSession(false);

        const locationState = location.state;
        const from = locationState?.from?.pathname || '/dashboard';
        const target =
          result.mustChangePassword ||
          result.user?.must_change_password ||
          localStorage.getItem('auth_must_change_password') === 'true'
            ? '/auth/complete-invited-password-setup'
            : result.verificationRequired
              ? '/verify-email'
              : from;

        navigate(target, { replace: true });
      } else {
        setError('Login failed. Invalid response from server.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error caught:', err);
      setError(err || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <ParticlesAuth>
        <Col xxl={6} className="mx-auto">
          <Card className="mb-0 border-0 shadow-none mb-0">
            <Card.Body className="p-sm-5">
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Checking session...</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </ParticlesAuth>
    );
  }

  return (
    <ParticlesAuth>
      <Col xxl={6} className="mx-auto">
        <Card className="mb-0 border-0 shadow-none mb-0">
          <Card.Body className="p-sm-5">
            <div className="text-center mt-5">
              <h5 className="fs-3xl">Welcome Back</h5>
              <p className="text-muted">Sign in to continue to Volksign.</p>
            </div>

            <div className="p-2 mt-5">
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Form
                onSubmit={handleSubmit}
              >
                <Form.Group className="mb-3" controlId="username">
                  <Form.Label>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    className="form-control bg-light border-light"
                    placeholder="Enter email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="password-input">
                  <div className="float-end">
                    <Link to="/forgot-password" className="text-muted">
                      Forgot password?
                    </Link>
                  </div>
                  <Form.Label>
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative auth-pass-inputgroup mb-3">
                    <Form.Control
                      type={passwordShow ? 'text' : 'password'}
                      name="password"
                      className="form-control bg-light border-light pe-5 password-input"
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                    />
                    <button
                      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                      type="button"
                      onClick={() => setPasswordShow(!passwordShow)}
                    >
                      <i className={passwordShow ? 'ri-eye-off-fill align-middle' : 'ri-eye-fill align-middle'}></i>
                    </button>
                  </div>
                </Form.Group>

                <Form.Check className="mb-3" id="auth-remember-check">
                  <Form.Check.Input type="checkbox" />
                  <Form.Check.Label>Remember me</Form.Check.Label>
                </Form.Check>

                <div className="mt-4">
                  <Button 
                    className="btn btn-primary w-100" 
                    type="submit" 
                    disabled={loading}
                  >
                    {loading && <Spinner size="sm" className="me-2" />}
                    Sign In
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-5">
                <p className="mb-0">
                  Don't have an account?{' '}
                  <Link to="/register" className="fw-semibold text-secondary text-decoration-underline">
                    Sign Up
                  </Link>
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </ParticlesAuth>
  );
}

export default Login;